"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ValidGridUnits = void 0;
exports.convertGridDistanceUnits = convertGridDistanceUnits;
exports.findComponentByFootprint = findComponentByFootprint;
exports.dedupeWarnings = dedupeWarnings;
exports.removeQuotes = removeQuotes;
exports.cleanMetadataVal = cleanMetadataVal;
exports.readMaxLines = readMaxLines;
exports.getBlobTextClean = getBlobTextClean;
exports.decodeWindows1252 = decodeWindows1252;
exports.latin1ToWindows1252 = latin1ToWindows1252;
exports.isWindows1252Representable = isWindows1252Representable;
exports.decodeWindows1252Clean = decodeWindows1252Clean;
exports.getBlobTextCleanAnsi = getBlobTextCleanAnsi;
exports.bytesToLatin1 = bytesToLatin1;
exports.getBlobTextCleanPartial = getBlobTextCleanPartial;
exports.getValueFromMetadata = getValueFromMetadata;
exports.isValidDistance = isValidDistance;
exports.toFractionalPrecision = toFractionalPrecision;
exports.maybeInvert = maybeInvert;
exports.formatValueForGrid = formatValueForGrid;
exports.formatPositionForGrid = formatPositionForGrid;
const Transform_1 = require("@cadstrom/sigma-engine-shared/dist/Utils/Transform");
const deepEqual = require("fast-deep-equal");
const MM_PER_INCH = 25.4;
exports.ValidGridUnits = ['inch', 'microns', 'mil', 'mm'];
function convertGridDistanceUnits(distance, fromUnits, toUnits) {
    if (fromUnits === toUnits) {
        return distance;
    }
    let distanceMM;
    switch (fromUnits) {
        case 'inch':
            distanceMM = distance * MM_PER_INCH;
            break;
        case 'mm':
            distanceMM = distance;
            break;
        case 'mil':
            distanceMM = (distance * MM_PER_INCH) / 1000;
            break;
        case 'microns':
            distanceMM = distance / 1000;
            break;
        default:
            throw new Error(`Invalid value ${fromUnits} for fromUnits`);
    }
    switch (toUnits) {
        case 'inch':
            return (distanceMM = distanceMM / MM_PER_INCH);
        case 'mm':
            return distanceMM;
        case 'mil':
            return (distanceMM = (distanceMM / MM_PER_INCH) * 1000);
        case 'microns':
            return (distanceMM = distanceMM * 1000);
        default:
            throw new Error(`Invalid value ${toUnits} for toUnits`);
    }
}
function findComponentByFootprint(project, footprint, model) {
    for (const component of Object.values(project.components)) {
        for (const fp of component.footprints) {
            if (fp.ref === footprint.ref) {
                if (!deepEqual(fp, footprint)) {
                    model.warnings.push(`Multiple definitions found for footprint '${footprint.name}'`);
                }
                return component;
            }
        }
    }
    for (const design of Object.values(project.designs)) {
        if (design.modelType !== 'schematic') {
            continue;
        }
        for (const module of design.modules) {
            if (module.drawProperties['Footprint']?.value === footprint.ref) {
                project.components[module.componentRef].footprints.push(footprint);
                return project.components[module.footprintRef];
            }
        }
    }
    return project.components[footprint.ref];
}
function dedupeWarnings(model) {
    const warnings = new Set();
    for (const warning of model.warnings) {
        if (!warnings.has(warning)) {
            warnings.add(warning);
        }
    }
    model.warnings = Array.from(warnings);
}
function removeQuotes(value) {
    return value?.replace(/^"|"$/g, '').trim();
}
function cleanMetadataVal(value) {
    value = value?.replace(/^(?:\s|\\r|\\n)+|(?:\s|\\r|\\n)+$/gm, '').trim();
    if (!value || value === '~') {
        return undefined;
    }
    return value;
}
function readMaxLines(contents, maxLines) {
    const lines = [];
    let lineStart = 0;
    for (let chrPos = 0; lines.length < maxLines && chrPos < contents.length; ++chrPos) {
        if (contents[chrPos] === '\n') {
            lines.push(contents.substring(lineStart, chrPos));
            lineStart = chrPos + 1;
        }
    }
    if (lines.length === 0) {
        return [contents];
    }
    return lines;
}
async function getBlobTextClean(blob) {
    return (await blob.text()).replace(/\r/g, '');
}
const windows1252C1Mappings = '\u20ac\u0081\u201a\u0192\u201e\u2026\u2020\u2021' +
    '\u02c6\u2030\u0160\u2039\u0152\u008d\u017d\u008f' +
    '\u0090\u2018\u2019\u201c\u201d\u2022\u2013\u2014' +
    '\u02dc\u2122\u0161\u203a\u0153\u009d\u017e\u0178';
function decodeWindows1252(bytes) {
    return latin1ToWindows1252(bytesToLatin1(bytes));
}
function latin1ToWindows1252(byteString) {
    return byteString.replace(/[\u0080-\u009f]/g, (char) => windows1252C1Mappings.charAt(char.charCodeAt(0) - 0x80));
}
const windows1252CodePoints = (() => {
    const codePoints = new Set();
    for (let byte = 0; byte <= 0xff; byte++) {
        codePoints.add(byte < 0x80 || byte > 0x9f ? byte : windows1252C1Mappings.charCodeAt(byte - 0x80));
    }
    return codePoints;
})();
function isWindows1252Representable(value) {
    for (const char of value) {
        const codePoint = char.codePointAt(0);
        if (codePoint === undefined || !windows1252CodePoints.has(codePoint)) {
            return false;
        }
    }
    return true;
}
function decodeWindows1252Clean(bytes) {
    return decodeWindows1252(bytes).replace(/\r/g, '');
}
async function getBlobTextCleanAnsi(blob) {
    return decodeWindows1252Clean(new Uint8Array(await blob.arrayBuffer()));
}
function bytesToLatin1(bytes) {
    const chunkSize = 0x8000;
    if (bytes.length <= chunkSize) {
        return Reflect.apply(String.fromCharCode, null, bytes);
    }
    const chunks = [];
    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
        chunks.push(Reflect.apply(String.fromCharCode, null, bytes.subarray(offset, offset + chunkSize)));
    }
    return chunks.join('');
}
async function getBlobTextCleanPartial(blob, maxBytes = 65536) {
    const slice = blob.slice(0, Math.min(maxBytes, blob.size));
    return (await slice.text()).replace(/\r/g, '');
}
function getValueFromMetadata(currentValue, metaModule) {
    const metadataRegex = /\${([^}]*)}/g;
    if (metaModule && metaModule.metadata && metadataRegex.test(currentValue)) {
        const valueKey = currentValue.substring(2, currentValue.length - 1);
        if (valueKey in metaModule.metadata) {
            return currentValue.replace(metadataRegex, (match, valueKey) => metaModule.metadata?.[valueKey]?.toString() ?? match);
        }
    }
    return currentValue;
}
function isValidDistance(val) {
    return typeof val === 'number' && Number.isFinite(val) && val > 0;
}
function toFractionalPrecision(val, precision, maxFractional = 8) {
    const valStr = val.toString();
    const [int, fractional] = valStr.split('.', 2);
    const [, precisionFractionalStr] = precision.toString().split('.', 2);
    const precisionFractional = Math.min(precisionFractionalStr?.length || 0, maxFractional);
    let retVal;
    if (!fractional) {
        retVal = int + '.'.padEnd(precisionFractional + 1, '0');
    }
    else {
        retVal = `${int}.${fractional.substring(0, precisionFractional).padEnd(precisionFractional, '0')}`;
    }
    if (retVal.endsWith('.')) {
        return retVal.substring(0, retVal.length - 1);
    }
    return retVal;
}
function maybeInvert(value, invert) {
    if (invert) {
        return -value;
    }
    return value;
}
function formatValueForGrid(value, grid, roundToGridIntervals = true) {
    if (isValidDistance(grid?.distance)) {
        return toFractionalPrecision(convertGridDistanceUnits(roundToGridIntervals ? (0, Transform_1.roundToMultiple)(value, grid.distance) : value, 'mm', grid.units), grid.distance);
    }
    return undefined;
}
function formatPositionForGrid(drawingCoords, grid) {
    if (isValidDistance(grid.distance)) {
        return {
            x: toFractionalPrecision(maybeInvert(convertGridDistanceUnits((0, Transform_1.roundToMultiple)(drawingCoords.x, grid.distance), 'mm', grid.units), grid.invertX), grid.distance),
            y: toFractionalPrecision(maybeInvert(convertGridDistanceUnits((0, Transform_1.roundToMultiple)(drawingCoords.y, grid.distance), 'mm', grid.units), grid.invertY), grid.distance),
        };
    }
    return undefined;
}
//# sourceMappingURL=Util.js.map